import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { adminApi, catalogApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const COLOR_PRESETS = [
  'bg-orange-50 text-orange-600',
  'bg-emerald-50 text-emerald-600',
  'bg-rose-50 text-rose-600',
  'bg-pink-50 text-pink-600',
  'bg-purple-50 text-purple-600',
  'bg-blue-50 text-blue-600',
  'bg-slate-100 text-slate-700',
  'bg-amber-50 text-amber-600',
];
const ICON_OPTIONS = ['Store', 'UtensilsCrossed', 'ShoppingBasket', 'Pill', 'Flower2', 'Wine', 'Cpu', 'Gift', 'Coffee', 'Pizza', 'IceCream', 'Apple'];

const empty = { id: '', name: '', icon: 'Store', color: COLOR_PRESETS[0] };

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { try { setCategories(await catalogApi.categories()); } catch { /* */ } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const { id, ...rest } = form;
        await adminApi.updateCategory(editing.id, rest);
        toast.success('Category updated');
      } else {
        if (!form.id) { toast.error('ID is required'); return; }
        await adminApi.createCategory(form);
        toast.success('Category created');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await adminApi.deleteCategory(c.id); toast.success('Category deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const PreviewIcon = Icons[form.icon] || Icons.Store;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Categories</h1>
          <p className="text-slate-500 mt-1">Storefront categories shown on the home page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-1.5" />New category</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit category' : 'Create category'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>ID</Label><Input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} disabled={!!editing} placeholder="bakery" className="h-10 mt-1" /></div>
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 mt-1" /></div>
              <div>
                <Label>Icon</Label>
                <div className="grid grid-cols-6 gap-2 mt-2">
                  {ICON_OPTIONS.map((nm) => {
                    const I = Icons[nm] || Icons.Store;
                    return (
                      <button key={nm} type="button" onClick={() => setForm({ ...form, icon: nm })}
                        className={`h-10 rounded-lg flex items-center justify-center border ${form.icon === nm ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-slate-200 hover:border-slate-300'}`}>
                        <I className="h-4 w-4 text-slate-700" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Color</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {COLOR_PRESETS.map((c) => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                      className={`h-10 rounded-lg flex items-center justify-center ${c} ${form.color === c ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}>
                      <PreviewIcon className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white">{editing ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        {categories.map((c) => {
          const I = Icons[c.icon] || Icons.Store;
          return (
            <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col items-center text-center">
              <div className={`h-12 w-12 rounded-full ${c.color} flex items-center justify-center`}><I className="h-6 w-6" /></div>
              <div className="mt-2 font-medium text-slate-900 text-sm">{c.name}</div>
              <div className="text-xs text-slate-400">{c.id}</div>
              <div className="mt-3 flex gap-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(c)}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(c)} className="text-rose-600 hover:text-rose-700 border-rose-200"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoriesPage;
